'use client';

import React from 'react';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';

interface SelectOption {
  value: string | number;
  label: string | React.ReactNode;
}

interface SelectProps {
  label?: string | React.ReactNode;
  error?: string;
  helperText?: string;
  options: SelectOption[];
  value: string | number;
  onChange: (value: string | number) => void;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
}

export default function Select({
  label,
  error,
  helperText,
  id,
  required,
  disabled,
  className = '',
  options,
  value,
  onChange,
}: SelectProps) {
  const { darkMode } = useDarkMode();

  // Generate a unique ID if not provided (using React.useId for SSR compatibility)
  const generatedId = React.useId();
  const selectId = id || generatedId;

  // Find the selected option
  const selectedOption = options.find((opt) => opt.value === value) || options[0];

  return (
    <div className={className}>
      <Listbox as="div" value={value} onChange={onChange} disabled={disabled}>
        {label && (
          <label
            htmlFor={selectId}
            className={`mb-2 block text-[13px] font-medium ${
              darkMode ? 'text-gray-400' : 'text-gray-600'
            }`}
          >
            {label}
            {required && <span className="ml-1 text-red-500">*</span>}
          </label>
        )}

        <div className="relative">
          <ListboxButton
            id={selectId}
            className={`block grid w-full cursor-default grid-cols-1 rounded-md px-3 py-1.5 text-left text-base outline-1 -outline-offset-1 transition-all sm:text-sm/6 ${
              darkMode
                ? 'bg-white/5 text-gray-100 outline-white/10'
                : 'bg-white text-gray-900 outline-gray-300'
            } ${
              darkMode
                ? 'focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-500'
                : 'focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600'
            } ${
              error
                ? darkMode
                  ? 'outline-red-500/50 focus:outline-red-500'
                  : 'outline-red-500 focus:outline-red-500'
                : ''
            } ${disabled ? 'disabled:cursor-not-allowed disabled:opacity-60' : ''} `}
          >
            <span className="col-start-1 row-start-1 flex items-center pr-6">
              <span className="block truncate">{selectedOption?.label}</span>
            </span>
            <ChevronUpDownIcon
              aria-hidden="true"
              className={`col-start-1 row-start-1 size-5 self-center justify-self-end ${
                darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}
            />
          </ListboxButton>

          <ListboxOptions
            anchor="bottom"
            transition
            className={`z-[150] mt-1 max-h-56 w-[var(--button-width)] overflow-auto rounded-md py-1 text-base shadow-lg ring-1 transition duration-100 ease-in focus:outline-hidden data-closed:opacity-0 sm:text-sm ${
              darkMode ? 'bg-gray-800 ring-white/10' : 'bg-white ring-black/5'
            } `}
          >
            {options.map((option) => (
              <ListboxOption
                key={option.value}
                value={option.value}
                className={`group relative cursor-default py-2 pr-3 pl-9 select-none data-focus:outline-hidden ${
                  darkMode
                    ? 'text-gray-100 data-focus:bg-indigo-600 data-focus:text-white'
                    : 'text-gray-900 data-focus:bg-indigo-600 data-focus:text-white'
                } `}
              >
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-600 group-not-data-selected:hidden group-data-focus:text-white">
                  <CheckIcon aria-hidden="true" className="size-5" />
                </span>

                <span className="block truncate font-normal group-data-selected:font-semibold">
                  {option.label}
                </span>
              </ListboxOption>
            ))}
          </ListboxOptions>
        </div>
      </Listbox>

      {(error || helperText) && (
        <p
          className={`mt-2 text-sm ${
            error ? 'text-red-500' : darkMode ? 'text-gray-400' : 'text-gray-600'
          }`}
        >
          {error || helperText}
        </p>
      )}
    </div>
  );
}
